import { z } from 'zod';
import {
  ClassIdSchema,
  DBClassClassIdSchema,
  DBClassSubjectClassIdSchema,
  DBClassSubjectSchema,
} from '../classes/schemas';
import { BILLING_TYPE_LABELS, BillingType, Location } from '../common/enums';
import { enumLabels } from '../common/fields';
import {
  HumanFullNameSchema,
  LocationSchema,
  StringBooleanSchema,
  UnixTimestampSchema,
} from '../common/schemas';
import {
  DBLessonTemplateCourseSchema,
  DBLessonTemplateDurationSchema,
  DBLessonTemplateLessonIdSchema,
  LessonTemplateIdSchema,
} from '../lessonTemplates/schemas';
import { QuestionSetIdSchema } from '../questionSets/schemas';
import { LessonEnrolmentIdSchema } from '../lessonEnrolments/schemas';
import { DBRoomRoomIdSchema, RoomIdSchema } from '../rooms/schemas';
import { StaffStaffIdSchema } from '../staff/schemas';
import { DBStreamStreamIdSchema } from '../streams/schemas';
import { DBStudentStudentIdSchema } from '../students/schemas';

/**
 * Shared & Helper Schemas
 */

export const DBLessonCounterSchema = z
  .string()
  .regex(/^[0-9]+$/)
  .length(4);
export const LessonCounterSchema = DBLessonCounterSchema;

/**
 * DB Schemas
 */

export const DBLessonSubjectClassIdentifierSchema = z.lazy(() => DBClassSubjectClassIdSchema);
export const DBLessonLessonSchema = z.string().refine(val => {
  const parts = val.split('#');
  return (
    parts.length === 3 &&
    z.lazy(() => DBLessonTemplateCourseSchema).safeParse(parts[0]).success &&
    z.lazy(() => DBLessonTemplateLessonIdSchema).safeParse(parts[1]).success &&
    DBLessonCounterSchema.safeParse(parts[2]).success
  );
}, 'Must be in format course#lessonId#0000');
export const DBLessonAbsenteeCountSchema = z.number().int().min(0);
export const DBLessonBillingTypeSchema = z
  .enum(BillingType)
  .register(enumLabels, { labels: BILLING_TYPE_LABELS });
export const DBLessonCourseLessonSchema = z.string().refine(val => {
  const parts = val.split('#');
  return (
    parts.length === 2 &&
    z.lazy(() => DBLessonTemplateCourseSchema).safeParse(parts[0]).success &&
    z.lazy(() => DBLessonTemplateLessonIdSchema).safeParse(parts[1]).success
  );
}, 'Must be in format course#lessonId');
export const DBLessonDurationSchema = z.lazy(() => DBLessonTemplateDurationSchema);
export const DBLessonEnrolledStudentsSchema = z.record(
  z.lazy(() => DBStudentStudentIdSchema),
  z
    .object({
      name: HumanFullNameSchema,
    })
    .strict()
);
export const DBLessonHomeworkMarkedSchema = z.boolean();
export const DBLessonLocationSchema = LocationSchema;
export const DBLessonMakeUpCountSchema = z.number().int().min(0);
export const DBLessonNotesSchema = z.string().min(0).max(4096);
export const DBLessonOnlineCountSchema = z.number().int().min(0);
export const DBLessonQuizSchema = z.lazy(() => QuestionSetIdSchema);
export const DBLessonRollMarkedSchema = StringBooleanSchema;
export const DBLessonRoomSchema = z.lazy(() => DBRoomRoomIdSchema);
export const DBLessonSeparatelyBilledSchema = z.boolean();
export const DBLessonStartDatetimeSchema = UnixTimestampSchema;
export const DBLessonStreamSchema = z.lazy(() => DBStreamStreamIdSchema);
export const DBLessonStudentCountSchema = z.number().int().min(0);
export const DBLessonTeacherSchema = z.lazy(() => StaffStaffIdSchema).or(z.literal(''));

export const DBLessonSchema = z.object({
  subjectClassIdentifier: DBLessonSubjectClassIdentifierSchema,
  lesson: DBLessonLessonSchema,
  absenteeCount: DBLessonAbsenteeCountSchema,
  billingType: DBLessonBillingTypeSchema,
  courseLesson: DBLessonCourseLessonSchema,
  duration: DBLessonDurationSchema,
  // Optional to allow removal of a specific student
  enrolledStudents: DBLessonEnrolledStudentsSchema.optional(),
  homeworkMarked: DBLessonHomeworkMarkedSchema,
  location: DBLessonLocationSchema,
  makeUpCount: DBLessonMakeUpCountSchema,
  notes: DBLessonNotesSchema,
  onlineCount: DBLessonOnlineCountSchema,
  // .optional(): set when a custom quiz is drafted from the lesson's roll, and when a quiz run from it finishes
  quiz: DBLessonQuizSchema.optional(),
  rollMarked: DBLessonRollMarkedSchema,
  room: DBLessonRoomSchema,
  separatelyBilled: DBLessonSeparatelyBilledSchema,
  startDatetime: DBLessonStartDatetimeSchema,
  stream: DBLessonStreamSchema,
  studentCount: DBLessonStudentCountSchema,
  teacher: DBLessonTeacherSchema,
});

/**
 * Client Schemas
 */

export const LessonIdSchema = z.string().brand<'LessonId'>();
export const LessonClassIdSchema = z.lazy(() => ClassIdSchema);
export const LessonLessonTemplateIdSchema = z.lazy(() => LessonTemplateIdSchema);
export const LessonSubjectSchema = z.lazy(() => DBClassSubjectSchema);
export const LessonClassCodeSchema = z.lazy(() => DBClassClassIdSchema);
export const LessonCourseIdSchema = z.lazy(() => DBLessonTemplateCourseSchema);
export const LessonLessonTemplateCodeSchema = z.lazy(() => DBLessonTemplateLessonIdSchema);
export const LessonClassLabelSchema = z.string();
export const LessonAbsenteeCountSchema = DBLessonAbsenteeCountSchema;
export const LessonBillingTypeSchema = DBLessonBillingTypeSchema;
export const LessonDurationSchema = DBLessonDurationSchema;
export const LessonEnrolledStudentsSchema = z.array(
  z
    .object({
      studentId: z.lazy(() => DBStudentStudentIdSchema),
      name: HumanFullNameSchema,
      lessonEnrolmentId: z.lazy(() => LessonEnrolmentIdSchema),
    })
    .strict()
);
export const LessonHomeworkMarkedSchema = DBLessonHomeworkMarkedSchema;
export const LessonLocationSchema = DBLessonLocationSchema;
export const LessonMakeUpCountSchema = DBLessonMakeUpCountSchema;
export const LessonNotesSchema = DBLessonNotesSchema;
export const LessonOnlineCountSchema = DBLessonOnlineCountSchema;
export const LessonQuestionSetIdSchema = DBLessonQuizSchema;
export const LessonRollMarkedSchema = z.boolean();
export const LessonRoomIdSchema = z.lazy(() => RoomIdSchema);
export const LessonSeparatelyBilledSchema = DBLessonSeparatelyBilledSchema;
export const LessonStartDatetimeSchema = DBLessonStartDatetimeSchema;
export const LessonStreamIdSchema = DBLessonStreamSchema;
export const LessonStudentCountSchema = DBLessonStudentCountSchema;
export const LessonTeacherIdSchema = z.lazy(() => StaffStaffIdSchema);

export const LessonSchema = z.object({
  id: LessonIdSchema,
  classId: LessonClassIdSchema,
  lessonTemplateId: LessonLessonTemplateIdSchema,
  subject: LessonSubjectSchema.meta({ title: 'Subject' }),
  classCode: LessonClassCodeSchema.meta({ title: 'Class code' }),
  courseId: LessonCourseIdSchema.meta({ title: 'Course' }),
  lessonTemplateCode: LessonLessonTemplateCodeSchema.meta({ title: 'Lesson' }),
  counter: LessonCounterSchema,
  // `cl-{courseId}-{classCode}`, from the lesson's own course
  classLabel: LessonClassLabelSchema,
  absenteeCount: LessonAbsenteeCountSchema.default(0).meta({ title: 'Absentee count' }),
  billingType: LessonBillingTypeSchema.default(BillingType.Term).meta({ title: 'Billing type' }),
  duration: LessonDurationSchema.meta({ title: 'Duration' }),
  enrolledStudents: LessonEnrolledStudentsSchema.default([]).meta({ title: 'Enrolled students' }),
  homeworkMarked: LessonHomeworkMarkedSchema.default(false).meta({ title: 'Homework marked' }),
  location: LessonLocationSchema.default(Location.Chatswood).meta({ title: 'Location' }),
  makeUpCount: LessonMakeUpCountSchema.default(0).meta({ title: 'Make up count' }),
  notes: LessonNotesSchema.default('').meta({ title: 'Notes' }),
  onlineCount: LessonOnlineCountSchema.default(0).meta({ title: 'Online count' }),
  questionSetId: LessonQuestionSetIdSchema.optional(),
  rollMarked: LessonRollMarkedSchema.default(false).meta({ title: 'Roll marked' }),
  roomId: LessonRoomIdSchema.meta({ title: 'Room' }),
  separatelyBilled: LessonSeparatelyBilledSchema.default(false).meta({
    title: 'Separately billed',
  }),
  startDatetime: LessonStartDatetimeSchema.meta({ title: 'Started at' }),
  streamId: LessonStreamIdSchema.meta({ title: 'Stream' }),
  studentCount: LessonStudentCountSchema.default(0).meta({ title: 'Student count' }),
  // Absent while unassigned; a write of null unassigns the teacher
  teacherId: LessonTeacherIdSchema.nullish().meta({ title: 'Teacher' }),
});
